import * as RadixDialog from "@radix-ui/react-dialog";
import styled from "styled-components";
import { useDialogStore } from "@shared/common/contexts/dialog/useDialogStore";
import React from "react";
import { AddPrefixToObject } from "@shared/common/types/AddPrefixToObject";

interface DialogProps {
  readonly children?: React.ReactNode;
  readonly dialogId: string;
  readonly title: string;
  readonly description?: string;
  readonly maxWidth?: string;
  readonly width?: string;
  readonly isOpen?: boolean; // Optional prop to control the open state externally which is useful for testing. Otherwise leave undefined
}

export function Dialog({
  dialogId,
  title,
  description,
  children,
  maxWidth,
  width,
  isOpen = false,
}: DialogProps): React.JSX.Element {
  const isDialogOpen = useDialogStore((state) => state.dialogs[dialogId] || isOpen);
  const closeDialog = useDialogStore((state) => state.closeDialog);

  return (
    <RadixDialog.Root open={isDialogOpen} onOpenChange={() => closeDialog(dialogId)}>
      <RadixDialog.Portal>
        <StyledOverlay />
        <StyledDialogContent $maxWidth={maxWidth} $width={width}>
          <StyledDialogTitle>{title}</StyledDialogTitle>
          {description ? (
            <StyledDescription>{description}</StyledDescription>
          ) : (
            // Empty description to prevent warning
            <RadixDialog.Description />
          )}
          {children}
          <StyledDialogClose asChild>
            <tractie-button level="tertiary" icon-only={true} aria-label="Sluiten">
              <tractie-icon name="close" color="--tractie-content-color-subtle" />
            </tractie-button>
          </StyledDialogClose>
        </StyledDialogContent>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

interface WithHeight {
  readonly height?: string;
}

interface DialogContentScrollableProps extends WithHeight, React.PropsWithChildren {}

export function DialogContentScrollable({
  children,
  height = "calc(85vh - 20px - (20px + 20px + 24px))", // 20px for DialogContent bottom padding, and 64px for DialogTitle
}: DialogContentScrollableProps) {
  return (
    <DialogContentContainer>
      <ScrollContainer $height={height}>{children}</ScrollContainer>
    </DialogContentContainer>
  );
}

export const ScrollContainer = styled.div<AddPrefixToObject<Required<WithHeight>, "$">>`
  height: ${({ $height }) => $height};
  padding: ${({ theme }) => `0 ${theme.spacing.spacing5.toString()}`};
  overflow-y: auto;
`;

export const DialogContentContainer = styled.div<{ $maxHeight?: string }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => `0 0 ${theme.spacing.spacing5.toString()}`};
  max-height: ${({ $maxHeight }) => $maxHeight ?? `initial`};
`;

export const DialogContent = styled.div<AddPrefixToObject<Required<WithHeight>, "$">>`
  height: ${({ $height }) => $height};
  padding: ${({ theme }) => `0 0 ${theme.spacing.spacing5.toString()}`};
`;

const StyledDescription = styled(RadixDialog.Description)`
  padding: ${({ theme }) =>
    `0 ${theme.spacing.spacing5.toString()} ${theme.spacing.spacing5.toString()}`};
`;

const StyledOverlay = styled(RadixDialog.Overlay)`
  display: flex;
  position: fixed;
  inset: 0;
  padding: ${({ theme }) => theme.spacing.spacing4.toString()};
  justify-content: center;
  gap: 150px;
  align-items: center;
  width: 100svw;
  background-color: rgba(0, 0, 0, 0.5);
  animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 99;
`;

const StyledDialogTitle = styled(RadixDialog.Title)`
  padding: var(--tractie-spacing-spacer-5);
  font-size: var(--tractie-typography-size-xl);
  margin: 0;
`;

const StyledDialogContent = styled(RadixDialog.Content)<{ $maxWidth?: string; $width?: string }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 0.75rem;
  width: ${({ $width }) => $width ?? "100%"};
  max-width: ${({ $maxWidth }) => $maxWidth ?? "48rem"};
  transition-duration: 300ms;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  backdrop-blur: blur(40px);
  backdrop-filter: saturate(180%) blur(20px);
  background-color: ${({ theme }) => theme.colors.backgroundElevated2};
  z-index: 100;

  & > div[role="alert"] {
    margin-bottom: ${({ theme }) => theme.spacing.spacing5.toString()};
  }
`;

const StyledDialogClose = styled(RadixDialog.DialogClose)`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;
